/**
 * سربرگِ فعالیتِ کاربران.
 *
 * جدولش (`app_events`) تازه در مایگریشن 0068 ساخته شده و کلاینت‌ها هنوز
 * چیزی نمی‌فرستند. پس تا مدتی همه‌ی اعداد صفرند — و صفحه **صریح می‌گوید**
 * که این «هیچ‌کس نیامده» نیست، «هنوز چیزی ثبت نمی‌شود» است.
 *
 * فرقشان مهم است: اولی یک یافته‌ی محصولی است و دومی یک کارِ ناتمام. صفحه‌ای
 * که این دو را یکی نشان دهد، آدم را دنبالِ باگی می‌فرستد که وجود ندارد.
 *
 * ═══ ⚠ اعدادِ این صفحه «بی خودمان» است ═══
 *
 * از مایگریشن 0119 حساب‌های داخلی — دو حسابِ صاحبِ پروژه با همه‌ی نام‌های
 * مستعارش، و کاربرهای تستی — از این آمار بیرون‌اند، و هر نشستی که یکی از
 * آن‌ها در آن دیده شده هم همین‌طور. پیش از آن، نزدیک به دو سومِ «رفتارِ
 * کاربران» رفتارِ خودمان بود.
 *
 * فیلتر **بی‌صدا نیست**: `excluded` می‌گوید چند رویداد و چند حساب کنار
 * رفت و خطِ زیرِ افسانه همان را می‌نویسد. صفحه‌ای که بی‌خبر فیلتر کند،
 * آدم را همان‌قدر گمراه می‌کند که صفحه‌ای که کم بشمارد.
 *
 * ═══ وب و موبایل، از مایگریشنِ 0148 ═══
 *
 * تا پیش از WHOSUP-242 هیچ عددِ این صفحه نمی‌گفت مالِ سایت است یا اپ، با
 * اینکه ستونش (`app_events.surface`) از روزِ اول پر می‌شد. حالا پیش‌فرض
 * همچنان «هر دو روی هم» است — چون سوالِ اولِ آدم «کلاً چه خبر بود» است —
 * ولی یک فیلترِ بالای صفحه هر تکه را جدا می‌کند، و دو کارتِ تازه مقایسه و
 * ناهم‌ترازی‌ها را نشان می‌دهند. تکه‌هایش در `surface.tsx` است.
 *
 * ═══ ⚠ این سربرگ «اجرا» ندارد ═══
 *
 * بقیه‌ی داشبورد ستونِ دومش «آخرین اجرای کرالر» است. این جدول را کاربر پر
 * می‌کند نه کرالر، پس ستونِ دومش **۲۴ ساعتِ اخیر** است — و همه‌جا هم دقیقاً
 * همین نوشته می‌شود. برچسبی که بگوید «آخرین اجرا» ولی چیزِ دیگری بشمارد از
 * ستونِ نداشته بدتر است.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { opsRepo, type AppActivity } from '@/core/data/opsRepo';
import {
  SESSION_RATIO_MAX, SESSION_RATIO_MIN, cardsFor, eventRows, isSurfaceFilter,
  sessionBalance, type SurfaceFilter,
} from '@/core/lib/activityReport';
import { useUrlState } from '@/app/useUrlState';
import { PairLegend, Stat, Two, fmtNum as fmt } from '@/ui/Num';
import { Journey } from './Journey';
import { DAY, DayBars, FunnelChart, OneSided, RetentionCard, SURFACE_FA, SurfaceChips, SurfaceCompare } from './surface';

/**
 * آمارِ بازدیدِ سایت جای دیگری زندگی می‌کند و این‌جا عمداً فقط یک لینک است.
 *
 * این سربرگ از `app_events` می‌خواند: کسی که **داخلِ** محصول است چه کرد.
 * کلادفلر جوابِ سوالِ قبل از آن را دارد — چند نفر اصلاً به سایت رسیدند و از
 * کجا آمدند — و آن داده در دیتابیسِ ما نیست.
 *
 * آوردنش به همین صفحه شدنی بود ولی یک توکنِ حسابِ کلادفلر می‌خواهد، و آن
 * توکن به کلِ حساب دسترسی دارد؛ داخلِ جاواسکریپتِ مرورگر گذاشتنش یعنی هر
 * کسی که سورس را باز کند برش می‌دارد. راهِ درستش یک واسطِ سمتِ سرور است —
 * تا روزی که واقعاً لازم شود، لینک صادق‌ترین کار است.
 *
 * ⚠ قرینه‌ی این لینک برای موبایل (نصب‌ها) در کنسولِ فروشگاه است و این‌جا
 * **لینک نمی‌شود**: آدرسِ آن صفحه را مطمئن نیستیم و فرستادنِ کسی به آدرسی
 * که شاید وجود نداشته باشد از نگفتنش بدتر است. کارتِ «چه چیزی فقط یک‌جا
 * ثبت می‌شود» همین را با کلمه می‌گوید.
 *
 * `:account` عمداً دست‌نخورده مانده: خودِ کلادفلر جایش شناسه‌ی حساب را
 * می‌گذارد، و این‌طور شناسه‌ی حساب داخلِ مخزن نمی‌نشیند.
 */
const CF_WEB_ANALYTICS = 'https://dash.cloudflare.com/?to=/:account/web-analytics';

export function ActivityTab() {
  const a = useQuery({ queryKey: ['activity'], queryFn: () => opsRepo.activity() });
  const [raw, setSurface] = useUrlState('surface', 'all', ['all', 'web', 'mobile']);
  const surface: SurfaceFilter = isSurfaceFilter(raw) ? raw : 'all';

  if (a.isLoading) return <p className="dim">…</p>;
  if (a.error) return <div className="card"><p className="err">{String(a.error)}</p></div>;

  const d = a.data!;
  const cards = cardsFor(d, surface);
  const empty = (cards.total.total ?? 0) === 0 && cards.total.d10 === 0;
  /**
   * ⚠ روی دیتابیسی که هنوز مایگریشنِ 0119 را ندارد این کلید نیست. خطِ
   * توضیح نیامدنش از ترکاندنِ کلِ سربرگ بهتر است — بقیه‌ی اعداد سرِ جای
   * خودشان درست‌اند.
   */
  const ex = d.excluded;
  /** همین‌طور 0148: بی آن، فیلترِ سطح همه‌جا صفر است و باید گفته شود. */
  const noSplit = !d.surfaces;
  const rows = eventRows(d, surface);
  const last = recent(d.recent, surface);

  return (
    <>
      <div className="topbar" style={{ borderBottom: 'none', paddingBottom: 0 }}>
        <SurfaceChips value={surface} onChange={(v) => setSurface(v)} />
        <span className="spacer" />
        <a className="chip" href={CF_WEB_ANALYTICS} target="_blank" rel="noreferrer">
          بازدیدِ سایت در کلادفلر ↗
        </a>
      </div>

      <PairLegend runLabel={DAY}
        note="این سربرگ اجرای کرالر ندارد، پس ستونِ دومش ۲۴ ساعتِ اخیر است." />

      {noSplit ? (
        <p className="note">
          ⚠ این دیتابیس هنوز مایگریشنِ ۰۱۴۸ را ندارد، پس تفکیکِ وب و موبایل
          در آن نیست: فیلترِ سطح همه‌جا صفر نشان می‌دهد. عددهای «هر دو»
          درست‌اند.
        </p>
      ) : null}

      {ex && ex.events > 0 ? (
        <p className="note">
          این اعداد <b>بی حساب‌های داخلی</b>اند: {fmt(ex.events)} رویداد از{' '}
          {fmt(ex.accounts)} حسابِ خودمان و تستی شمرده نشد
          {ex.names.length ? ` — ${ex.names.join('، ')}` : ''}. نشستی که یکی از
          آن‌ها در آن وارد شده باشد هم کامل کنار می‌رود، وگرنه «مهمان»ش
          می‌ماند. تغییرِ این فهرست: مایگریشنِ 0119، جدولِ{' '}
          <span className="mono">internal_accounts</span>.
        </p>
      ) : null}

      {empty ? (
        <p className="note">
          {surface !== 'all'
            ? `در این پنجره هیچ رویدادی از ${SURFACE_FA[surface]} ثبت نشده.`
            : ex && ex.events > 0
              ? 'هرچه در این پنجره ثبت شده مالِ حساب‌های داخلی بوده — یعنی هیچ‌کسِ '
                + 'بیرونی اپ را باز نکرده، نه اینکه چیزی ثبت نمی‌شود.'
              : 'هنوز هیچ رویدادی ثبت نشده — نه از اپ، نه از سایت.'}
        </p>
      ) : null}

      <div className="grid n4" style={{ marginTop: 12 }}>
        <Stat label={label('رویداد', surface)} v={cards.total} runLabel={DAY} />
        <Stat label={label('نشست', surface)} v={cards.sessions} runLabel={DAY} />
        <Stat label={label('کاربرِ واردشده', surface)} v={cards.people} runLabel={DAY} />
        <Stat label={label('مهمان', surface)} v={cards.guests} runLabel={DAY} />
      </div>

      <SessionBalanceNote a={d} surface={surface} />

      {/* ⚠ فقط در حالتِ «هر دو»: زیرِ فیلتر، همان ردیف در کارت‌های بالا هست. */}
      {surface === 'all' ? <SurfaceCompare a={d} /> : null}

      <DayBars a={d} filter={surface} />

      <FunnelChart filter={surface} />

      <RetentionCard filter={surface} />

      {/* ═══ سفرِ کاربر — مایگریشن ۰۱۲۰ ═══
          بالای «پرتکرارترین رویدادها» می‌نشیند چون سوالِ بزرگ‌تری جواب
          می‌دهد: آن جدول می‌گوید چه اتفاق‌هایی افتاد، این می‌گوید آدم‌ها
          چه مسیری رفتند. */}
      <Journey surface={surface} />

      {/* ⚠ این کارت به فیلتر بی‌اعتناست و باید باشد: «فقط از وب آمده» حرفی
          درباره‌ی هر دو سطح است، پس زیرِ فیلترِ موبایل هم همان فهرست است. */}
      {surface === 'all' ? <OneSided a={d} /> : null}

      <div className="card">
        <h2>پرتکرارترین رویدادها{surface === 'all' ? '' : ` — ${SURFACE_FA[surface]}`}</h2>
        {rows.length === 0 ? <p className="dim">—</p> : (
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th>رویداد</th>
                  <th className="num">تعداد</th>
                  <th className="num">نشست</th>
                  {/* دو ستونِ آخر فقط وقتی معنی دارند که هر دو سطح در جدول
                      باشند؛ زیرِ فیلتر، ستونِ «تعداد» خودش همان است. */}
                  {surface === 'all' ? <th className="num">وب</th> : null}
                  {surface === 'all' ? <th className="num">موبایل</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.name}>
                    <td className="mono">{r.name}</td>
                    {/* ⚠ ردیفِ این جدول از اولش ده‌روزه است، پس ستونِ «کل»
                        ندارد و `Two` هم بی‌آن می‌آید. */}
                    <td className="num"><Two v={r.counts} runLabel={DAY} /></td>
                    <td className="num">{fmt(r.sessions)}</td>
                    {surface === 'all' ? (
                      <td className={`num${r.side === 'mobile' ? ' dim' : ''}`}>{fmt(r.web)}</td>
                    ) : null}
                    {surface === 'all' ? (
                      <td className={`num${r.side === 'web' ? ' dim' : ''}`}>{fmt(r.mobile)}</td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>نمونه‌ی آخرین رویدادها</h2>
        {/* ⚠ نمونه‌ی ده‌تاییِ **هر دو** سطح از دیتابیس می‌آید و این‌جا فیلتر
            می‌شود، پس زیرِ فیلتر کمتر از ده ردیف می‌ماند. گرفتنِ ده‌تای
            هر سطح یک کوئریِ دیگر می‌خواست برای یک جدولِ نمونه. */}
        {last.length === 0 ? <p className="dim">—</p> : (
          <div className="scrollx">
            <table className="tbl">
              <thead><tr><th>رویداد</th><th>سطح</th><th>props</th><th>زمان</th></tr></thead>
              <tbody>
                {last.map((r, i) => (
                  <tr key={i}>
                    <td className="mono">{r.name}</td>
                    <td>
                      {r.surface === 'web' || r.surface === 'mobile' ? (
                        <>
                          <span className={`swatch ${r.surface}`} />
                          {SURFACE_FA[r.surface]}
                        </>
                      ) : <span className="dim">{r.surface}</span>}
                    </td>
                    <td className="mono">{JSON.stringify(r.props)}</td>
                    {/* تاریخِ لاتین در سندِ راست‌به‌چپ باید جزیره‌ی خودش باشد،
                        وگرنه «p.m.» از انتهای زمان کنده می‌شود. */}
                    <td className="dim ltr">{new Date(r.created_at).toLocaleString('en-CA')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * هشدارِ کوچکِ زیرِ کارتِ «نشست» (WHOSUP-296): فقط وقتی نسبتِ پایان به
 * شروع روی یک سطح بیرون از بازه است دیده می‌شود. حکم و آستانه در
 * `activityReport.ts` است؛ این‌جا فقط نشان داده می‌شود.
 */
function SessionBalanceNote({ a, surface }: { a: AppActivity; surface: SurfaceFilter }) {
  const off = sessionBalance(a)
    .filter((b) => b.verdict === 'off' && (surface === 'all' || b.surface === surface));
  if (off.length === 0) return null;
  return (
    <div className="note">
      ⚠ شمارشِ نشست‌ها مشکوک است:{' '}
      {off.map((b, i) => (
        <span key={b.surface}>
          {i ? '، ' : ''}{SURFACE_FA[b.surface]} {fmt(b.end)} پایان در برابرِ {fmt(b.start)} شروع
        </span>
      ))}
      .
      <details>
        <summary>بیشتر</summary>
        هر نشست باید یک <span className="mono">session_start</span> و یک{' '}
        <span className="mono">session_end</span> بفرستد، پس نسبتِ این دو در ده روزِ اخیر باید
        نزدیکِ یک باشد. بیرون از {SESSION_RATIO_MIN} تا {SESSION_RATIO_MAX} یعنی خودِ ثبتِ
        نشست در کلاینت خراب است و عددِ کارتِ «نشست» قابلِ اعتماد نیست.
        {off.map((b) => (
          <div key={b.surface}>
            {SURFACE_FA[b.surface]}: نسبت{' '}
            {b.ratio === null ? '— (شروعی ثبت نشده)' : b.ratio.toFixed(2)}
          </div>
        ))}
      </details>
    </div>
  );
}

/** برچسبِ کارت —زیرِ فیلتر نامِ سطح رویش می‌نشیند، وگرنه عدد بی‌صاحب است. */
const label = (base: string, surface: SurfaceFilter): React.ReactNode =>
  (surface === 'all' ? base : <>{base} · {SURFACE_FA[surface]}</>);

type RecentRow = { name: string; surface: string; props: unknown; created_at: string };

const recent = (rows: RecentRow[], surface: SurfaceFilter): RecentRow[] =>
  (surface === 'all' ? rows : rows.filter((r) => r.surface === surface));
